/**
 * NestedListItem component for individual navigation items with indicators
 * Extracted from NestedList for better code organization
 */

import React, { memo } from 'react';
import {
  ListItem,
  ListItemText,
  ListItemButton,
} from '@mui/material';
import useNavigationState from '../hooks/ui/useNavigationState';
import IndicatorCheckbox from './IndicatorCheckbox';

/**
 * NestedListItem component for displaying individual navigation items
 * @param {Object} props - Component props
 * @param {string} props.itemTitle - Title of the item
 * @param {string} props.itemKey - Key of the item
 * @param {boolean} props.isIndicator - Whether the item is an indicator
 * @param {Function} props.scrollTo - Function to scroll to element
 * @returns {JSX.Element} NestedListItem component
 */
const NestedListItem = ({
  itemTitle,
  itemKey,
  isIndicator,
  scrollTo,
}) => {
  const { itemsStates, indicatorsWithStatus, toggleItem } = useNavigationState();

  const isFixedIndicator = indicatorsWithStatus[itemKey];

  // Don't render if this is an indicator but not available for current entity
  if (isIndicator && !(itemKey in indicatorsWithStatus)) {
    return null;
  }

  // Determine indicator color based on state
  let indicatorColor = 'grey'; // for text which is not indicator
  if (isFixedIndicator) {
    indicatorColor = '#02568d'; // fixed indicator
  } else if (isIndicator) {
    indicatorColor = '#009f98'; // optional indicator
  }

  /**
   * Handle click on item (different behavior for indicators)
   */
  const handleItemClick = () => {
    const isSelected = itemsStates[itemKey];

    // If an indicator is not selected, click triggers its selection
    if (isIndicator && !isSelected) {
      toggleItem(itemKey);
    }
  };

  const checkbox = (
    <IndicatorCheckbox
      itemKey={itemKey}
      isFixedIndicator={isFixedIndicator}
      color={indicatorColor}
      scrollTo={scrollTo}
    />
  );

  return (
    <ListItem
      key={`list-item-${itemKey}`}
      disablePadding
      sx={{ paddingLeft: 2 }}
      secondaryAction={Boolean(isIndicator) && checkbox}
    >
      <ListItemButton
        component="a"
        href={`#${itemKey}`}
        key={`list-item-button-${itemKey}`}
        onClick={handleItemClick}
        dense
      >
        <ListItemText
          id={`title-${itemKey}`}
          primary={itemTitle}
          primaryTypographyProps={{
            style: { color: indicatorColor },
            fontWeight: itemsStates[itemKey] ? 'bold' : 'normal',
          }}
        />
      </ListItemButton>
    </ListItem>
  );
};

NestedListItem.displayName = 'NestedListItem';

export default memo(NestedListItem);
